import { useState } from 'react';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const RequirementsStep = ({ formData, onChange, errors }) => {
  const [uploadedImages, setUploadedImages] = useState(formData?.images || []);

  const verificationLevels = [
    { value: 'any', label: 'Any Worker', description: 'No verification required' },
    { value: 'basic', label: 'Basic Verified', description: 'ID and phone verified' },
    { value: 'full', label: 'Fully Verified', description: 'ID, phone, and background check' },
  ];

  const requiredSkills = [
    'Licensed Professional',
    'Previous Experience Required',
    'Own Tools/Equipment',
    'Valid ID Required',
    'Background Check Passed',
    'References Available',
  ];

  const handleImageUpload = (e) => {
    const files = Array.from(e?.target?.files);
    const newImages = files?.map(file => ({
      url: URL.createObjectURL(file),
      alt: `Reference image showing ${file?.name?.split('.')?.[0]} for job requirements`,
      name: file?.name,
    }));
    const updated = [...uploadedImages, ...newImages]?.slice(0, 5);
    setUploadedImages(updated);
    onChange('images', updated);
  };

  const removeImage = (index) => {
    const updated = uploadedImages?.filter((_, i) => i !== index);
    setUploadedImages(updated);
    onChange('images', updated);
  };

  const handleSkillToggle = (skill) => {
    const currentSkills = formData?.requiredSkills || [];
    const updated = currentSkills?.includes(skill)
      ? currentSkills?.filter(s => s !== skill)
      : [...currentSkills, skill];
    onChange('requiredSkills', updated);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="bg-warning/10 border border-warning/20 rounded-md p-4 md:p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-warning/20 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xl">⚡</span>
          </div>
          <div>
            <h4 className="font-semibold text-foreground mb-1">Set Clear Expectations</h4>
            <p className="text-sm text-muted-foreground">
              Detailed requirements help workers understand if they're qualified. This reduces mismatched applications and saves everyone time.
            </p>
          </div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          Verification Level <span className="text-error">*</span>
        </label>
        <div className="space-y-3">
          {verificationLevels?.map((level) => (
            <label
              key={level?.value}
              className={`
                flex items-start gap-3 p-4 rounded-md border transition-smooth cursor-pointer
                ${formData?.verificationLevel === level?.value
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }
              `}
            >
              <input
                type="radio"
                name="verificationLevel"
                value={level?.value}
                checked={formData?.verificationLevel === level?.value}
                onChange={(e) => onChange('verificationLevel', e?.target?.value)}
                className="w-4 h-4 text-primary mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-foreground">{level?.label}</div>
                <div className="text-sm text-muted-foreground mt-1">{level?.description}</div>
              </div>
            </label>
          ))}
        </div>
        {errors?.verificationLevel && (
          <p className="text-sm text-error mt-2">{errors?.verificationLevel}</p>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          Required Skills & Qualifications
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {requiredSkills?.map((skill) => (
            <Checkbox
              key={skill}
              label={skill}
              checked={(formData?.requiredSkills || [])?.includes(skill)}
              onChange={() => handleSkillToggle(skill)}
            />
          ))}
        </div>
      </div>
      <Input
        label="Minimum Experience (Years)"
        type="number"
        placeholder="2"
        value={formData?.minExperience || ''}
        onChange={(e) => onChange('minExperience', e?.target?.value)}
        min="0"
        max="20"
        description="Leave blank if no experience required"
      />
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Special Instructions
        </label>
        <textarea
          value={formData?.specialInstructions || ''}
          onChange={(e) => onChange('specialInstructions', e?.target?.value)}
          placeholder="Any additional requirements or instructions for workers:\n• Safety equipment needed\n• Specific materials to bring\n• Access instructions\n• Parking information"
          className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
          rows={5}
          maxLength={500}
        />
        <p className="text-xs text-muted-foreground mt-2">
          {(formData?.specialInstructions || '')?.length}/500 characters
        </p>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          Reference Images (Optional)
        </label>
        <p className="text-sm text-muted-foreground mb-3">
          Upload photos showing what needs to be done. Maximum 5 images.
        </p>
        
        {uploadedImages?.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            {uploadedImages?.map((image, index) => (
              <div key={index} className="relative group">
                <Image
                  src={image?.url}
                  alt={image?.alt}
                  className="w-full h-32 object-cover rounded-md"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-2 right-2 w-8 h-8 bg-error text-error-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-smooth"
                  aria-label="Remove image"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {uploadedImages?.length < 5 && (
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-md cursor-pointer hover:border-primary transition-smooth bg-muted/30">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <Icon name="Upload" size={32} color="var(--color-muted-foreground)" />
              <p className="mt-2 text-sm text-muted-foreground">
                <span className="font-semibold">Click to upload</span> or drag and drop
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PNG, JPG up to 5MB ({5 - uploadedImages?.length} remaining)
              </p>
            </div>
            <input
              type="file"
              className="hidden"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
            />
          </label>
        )}
      </div>
      <Checkbox
        label="I agree to DISKARTE's Terms of Service and will treat workers with respect"
        checked={formData?.agreeToTerms || false}
        onChange={(e) => onChange('agreeToTerms', e?.target?.checked)}
        error={errors?.agreeToTerms}
        required
      />
    </div>
  );
};

export default RequirementsStep;